import React, { useContext } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation,
  PAGE_ROUTE_SOPS
} from "@kivunova/kivufrontendcommon";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import {
  LearnMoreCtaSection,
  LearnMoreHeroImage
} from "./LearnMoreContentViews";
import { LEARN_MORE_HUB_CATEGORIES } from "./constants/learnMoreHubContent";
import {
  publicInfoBodySx,
  publicInfoFeatureCardSx,
  publicInfoFeatureIconSx,
  publicInfoLinkSx,
  publicInfoSectionTitleSx
} from "./constants/publicInfoPageStyles";

const LEARN_MORE_ENTRY_ICONS: Record<string, React.ElementType> = {
  overview: InfoOutlinedIcon,
  regions: PublicOutlinedIcon,
  inventory: Inventory2OutlinedIcon,
  operations: StorefrontOutlinedIcon,
  finance: AssessmentOutlinedIcon,
  team: GroupsOutlinedIcon,
  platform: CloudOutlinedIcon,
  industries: BusinessOutlinedIcon,
  benefits: TrendingUpOutlinedIcon,
  "about-product": CompareArrowsOutlinedIcon,
  categories: CategoryOutlinedIcon
};

const BlogPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const pathFor = (route: string) =>
    language != null && defaultLang != null
      ? localizedPath(route, language, defaultLang)
      : `/${route}`;

  const userGuidePath = pathFor(PAGE_ROUTE_USER_GUIDE);
  const runBooksPath = pathFor(PAGE_ROUTE_SOPS);
  const contactPath = pathFor(PAGE_ROUTE_CONTACT);

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="learn-more-page"
        eyebrow={t("blog-page-eyebrow", "For business owners and teams")}
        title={t("blog-page-title", "Learn more about Kivunova FMS")}
        intro={t(
          "blog-page-intro",
          "Kivunova FMS helps your team manage products, stock, and sales across all stores. Pick one topic below—each page focuses on a single area."
        )}
      />

      <LearnMoreHeroImage />

      <PublicInfoCallout testId="learn-more-hub-note">
        {t(
          "learn-more-hub-note",
          "These pages explain how Kivunova FMS works in plain language. For step-by-step setup, read the User guide. For screenshot walkthroughs, open Run books."
        )}
      </PublicInfoCallout>

      <Stack spacing={3.5} sx={{ mt: 3 }}>
        {LEARN_MORE_HUB_CATEGORIES.map((category) => (
          <Box
            key={category.id}
            component="section"
            data-testid={`learn-more-hub-category-${category.id}`}
          >
            <Typography component="h2" sx={publicInfoSectionTitleSx}>
              {t(category.titleKey, category.titleFallback)}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)"
                },
                gap: { xs: 1.5, sm: 2 },
                mt: 1.5
              }}
            >
              {category.entries.map((entry) => {
                const Icon =
                  LEARN_MORE_ENTRY_ICONS[entry.id] ?? InfoOutlinedIcon;
                const entryPath = pathFor(entry.route);

                return (
                  <MuiLink
                    key={entry.id}
                    component={Link}
                    to={entryPath}
                    underline="none"
                    color="inherit"
                    data-testid={`learn-more-hub-entry-${entry.id}`}
                    sx={{
                      display: "block",
                      height: "100%",
                      "&:hover .learn-more-hub-entry-arrow": {
                        transform: "translateX(4px)"
                      }
                    }}
                  >
                    <Box sx={publicInfoFeatureCardSx}>
                      <Box sx={publicInfoFeatureIconSx}>
                        <Icon fontSize="small" />
                      </Box>
                      <Typography
                        component="h3"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: "0.9375rem", sm: "1rem" },
                          mb: 0.5
                        }}
                      >
                        {t(entry.titleKey, entry.titleFallback)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(entry.descriptionKey, entry.descriptionFallback)}
                      </Typography>
                      <ArrowForwardIcon
                        className="learn-more-hub-entry-arrow"
                        sx={{
                          position: "absolute",
                          right: 16,
                          top: 16,
                          fontSize: 18,
                          color: "primary.main",
                          transition: "transform 0.2s ease"
                        }}
                      />
                    </Box>
                  </MuiLink>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>

      <PublicInfoCtaPanel
        title={t("learn-more-hub-cta-title", "Need a path to follow?")}
        testId="learn-more-hub-cta"
      >
        <Typography variant="body1" sx={publicInfoBodySx}>
          <MuiLink
            component={Link}
            to={userGuidePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("learn-more-hub-cta-user-guide", "User guide")}
          </MuiLink>
          {t("learn-more-hub-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={runBooksPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("learn-more-hub-cta-run-books", "Run books")}
          </MuiLink>
          {t("learn-more-hub-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={contactPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("learn-more-hub-cta-contact", "Contact us")}
          </MuiLink>
        </Typography>
      </PublicInfoCtaPanel>

      <Box sx={{ mt: 4 }}>
        <LearnMoreCtaSection />
      </Box>
    </PublicMarketingLayout>
  );
};

export default BlogPage;
